import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { AvailabilityForm } from '@/components/shared/availability/AvailabilityForm';
import { TSTitleCard } from '@/components/shared/card/Card';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { breadcrumbName } from '@/helpers/Utility';
import { useSaveEmployeeSchedule } from '@/services/availability/useSaveEmployeeSchedule';
import { EmployeeSchedule } from '@/services/employee/schedule/schedule.types';
import { useEmployeeSchedule } from '@/services/employee/schedule/useEmployeeSchedule';
import { ReadEmployee, useGetStaffByIdQuery } from '@/services/gql/graphql.generated';
import { strings } from '@/services/translation/strings';
import { format } from 'date-fns';
import { FC, Suspense, useMemo } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { AiOutlineCalendar } from 'react-icons/ai';
import { useNavigate, useParams } from 'react-router-dom';
import dayStrings from '../../team.translations';
import { TeamMemberHeader } from '../TeamMemberHeader';
import { TeamMemberProvider } from '../TeamMemberService';

interface FormValues {
  availabilityType: string;
  startTime: string;
  endTime: string;
}

const TeamMemberCalendarDayViewEditAvailability: FC = () => {
  const { userId } = useParams<'userId'>();
  const { date } = useParams<'date'>();

  if (!userId) throw Error('User id should be provided in url params');
  if (!date) throw Error('Date should be provided in url params');

  const dateObj = useMemo(() => new Date(date), [date]);

  const [{ data: userData, error: userError }] = useGetStaffByIdQuery({
    variables: { id: userId }
  });

  const handleError = useErrorHandler();
  if (userError) handleError(userError);

  const employee = userData?.employee as ReadEmployee | undefined;

  const calendarBreadcrumbPath = `/team-member/${userId}/calendar-details/${date}`;

  const breadcrumbs = [
    { name: dayStrings.teamPageTitle, to: '/team' },
    {
      name: employee ? breadcrumbName(employee, dayStrings.employeeName) : dayStrings.employeeName('', ''),
      to: `/team-member/${userId}`
    },
    {
      name: (
        <div className="flex items-center space-x-2">
          <AiOutlineCalendar />
          <p>{format(dateObj, 'EEEE do MMMM yyyy')}</p>
        </div>
      ),
      to: calendarBreadcrumbPath
    },
    { name: strings.timeshift.teamMember.calendarDayView.editAvailability.title, to: '' }
  ];

  const navigate = useNavigate();

  const data = useEmployeeSchedule(userId, dateObj, dateObj);
  const schedule = data.schedule[date];

  const { save, fetching } = useSaveEmployeeSchedule(userId, dateObj, dateObj);

  const onSave = async (payload: EmployeeSchedule) => {
    await save(payload);
    navigate(calendarBreadcrumbPath);
  };

  return (
    <TeamMemberProvider>
      <PageContainer customHeader={<TeamMemberHeader employee={employee} breadcrumbs={breadcrumbs} />}>
        <Suspense fallback={<TableLoader />}>
          <div className="overflow-y-hidden flex flex-col bg-white">
            <PageSizer mode={SizerMode.PAGE} className="flex-1 overflow-auto space-y-6">
              <TSTitleCard
                title={format(dateObj, 'EEEE do MMMM yyyy')}
                descriptor={strings.timeshift.teamMember.calendarDayView.editAvailability.title}
                icon={AiOutlineCalendar}
              >
                <AvailabilityForm
                  date={dateObj}
                  existingDay={schedule}
                  fetching={fetching}
                  onSave={onSave}
                  onCancel={() => navigate(-1)}
                />
              </TSTitleCard>
            </PageSizer>
          </div>
        </Suspense>
      </PageContainer>
    </TeamMemberProvider>
  );
};

export default TeamMemberCalendarDayViewEditAvailability;
